import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import Alert from '../components/Alert';
import Icon from '../components/Icon';
import { defaultHomePath, useAuth } from '../context/AuthContext';

function LoginPage() {
  const { login, user } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  if (user) {
    return <Navigate to={defaultHomePath(user)} replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setMessage({ type: '', text: '' });
    try {
      const loggedIn = await login(username.trim(), password);
      // Navigation happens via defaultHomePath when user state updates.
      void loggedIn;
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string') {
        setMessage({ type: 'error', text: detail });
      } else if (!err.response) {
        setMessage({
          type: 'error',
          text: 'Cannot reach the API server. Start the backend (port 8000) and try again.',
        });
      } else {
        setMessage({
          type: 'error',
          text: 'Login failed. Check username and password.',
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="login-page">
      <aside className="login-aside">
        <span className="brand-link">
          <span className="brand-mark" aria-hidden="true">SC</span>
          <span className="brand-text">
            <span className="brand-title">Satit &amp; Cruce</span>
            <span className="brand-tagline">Talent platform</span>
          </span>
        </span>
        <div className="login-pitch">
          <h2>Hiring operations, run from one place.</h2>
          <p>
            Tailor resumes to every role, keep interviews on schedule, and follow each
            opportunity from first application to final outcome.
          </p>
          <ul className="login-features">
            <li>
              <span className="login-feature-icon"><Icon name="sparkles" /></span>
              <div>
                <strong>Tailored resumes</strong>
                <span>Generate role-specific resumes and cover letters in bulk.</span>
              </div>
            </li>
            <li>
              <span className="login-feature-icon"><Icon name="calendar" /></span>
              <div>
                <strong>Interview pipeline</strong>
                <span>Schedule, calendar, and round-by-round history per candidate.</span>
              </div>
            </li>
            <li>
              <span className="login-feature-icon"><Icon name="mail" /></span>
              <div>
                <strong>Inbox &amp; outcomes</strong>
                <span>Track recruiter replies and next steps across mailboxes.</span>
              </div>
            </li>
          </ul>
        </div>
        <p className="login-aside-foot">&copy; {new Date().getFullYear()} Satit &amp; Cruce</p>
      </aside>

      <main className="login-main">
        <div className="card login-card">
          <span className="brand-link login-card-mark">
            <span className="brand-mark" aria-hidden="true">SC</span>
            <span className="brand-title">Satit &amp; Cruce</span>
          </span>
          <h1>Sign in</h1>
          <p className="login-card-sub">Welcome back. Enter your workspace credentials.</p>
          <Alert message={message} />
          <form className="login-form" onSubmit={handleSubmit}>
            <label>
              Username
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoComplete="username"
                autoFocus
                required
              />
            </label>
            <label>
              Password
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
              />
            </label>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? 'Signing in…' : 'Sign in'}
            </button>
          </form>
          <p className="login-card-foot">Access is managed by your administrator.</p>
        </div>
      </main>
    </div>
  );
}

export default LoginPage;
